/**
 * The plan header's "NOS Demand" chip (workspace feat-link-resource-plans-to-nos1-demands).
 *
 * A plan can be tied to one NOS Demand on its Account. Once tied, the months NOS has
 * closed hold NOS's hours and are read-only in the grid, and the planner sends the open
 * months back with Post to NOS. The chip sits in the status row beside the NOS Deal,
 * the same shape, so the two read as one strip of "where this plan stands in NOS".
 *
 * Everything that writes goes through a preview first — what linking does to the closed
 * months, or every Resource × month a post would change — and applies exactly what was
 * shown (the server refuses a stale preview). Unlinking changes nothing in NOS. An unlinked
 * plan with no Demand yet links to Demand Planning's Create demand page, then picks the new Demand here.
 */

import { ChevronDown, Link2, Lock, Plus, RefreshCw, Send, Unlink } from "lucide-react";
import { useState } from "react";
import { formatMonthShort } from "@/components/demands/month-format";
import { STATUS_TRIGGER_CLASS, StatusDot } from "@/components/plans/PlanStatusControl";
import { ErrorState } from "@/components/shared/error-state";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useLinkNosDemand,
  useNosDemandLinkPreview,
  useNosGuidancePostPreview,
  usePlanNosDemandOptions,
  usePostNosGuidance,
  useUnlinkNosDemand,
} from "@/hooks/use-plan-nos-demand";
import { nosDemandLinkBlock } from "@/lib/plan-nos-demand";
import { cn } from "@/lib/utils";
import type { PlanNosDemandLinkPreview } from "@/schemas/plan-nos-demand";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

type Open = "link" | "refresh" | "post" | "unlink" | null;

/** `["2027-01","2027-02","2027-03"]` → `Jan 2027 – Mar 2027`. */
function monthSpan(months: readonly string[]): string {
  const first = months[0];
  const last = months[months.length - 1];
  if (first === undefined || last === undefined) return "";
  return first === last
    ? formatMonthShort(first)
    : `${formatMonthShort(first)} – ${formatMonthShort(last)}`;
}

/** `"40.00"` → `40`, `"12.50"` → `12.5`. */
function trimHours(hours: string): string {
  return hours.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

export function PlanNosDemandChip({ plan }: { plan: ResourcePlanDetailView }) {
  const [open, setOpen] = useState<Open>(null);
  const link = plan.nosDemand ?? null;
  const canEdit = plan.canEdit;
  // A new link, or a change of Demand, waits until Resourcing marks the plan Staffed
  // (2026-10-28). A linked plan keeps its link, and refresh / post / unlink stay open.
  const linkBlock = nosDemandLinkBlock(plan.resourcingStatus);

  if (link === null) {
    // Readers see nothing: a plan with no Demand has nothing to say about NOS guidance.
    if (!canEdit) return null;
    if (linkBlock !== null) {
      return (
        <span
          className={cn(
            STATUS_TRIGGER_CLASS,
            "cursor-default border-dashed text-muted-foreground shadow-none hover:bg-transparent",
          )}
          title={linkBlock}
        >
          <Link2 className="h-3.5 w-3.5" aria-hidden />
          Link NOS Demand · once Staffed
        </span>
      );
    }
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen("link")}
          className={cn(STATUS_TRIGGER_CLASS, "border-dashed text-muted-foreground shadow-none")}
          title="Tie this plan to a NOS Demand: closed months take NOS's hours, and open months can be posted back"
        >
          <Link2 className="h-3.5 w-3.5" />
          Link NOS Demand
        </button>
        {open === "link" && <LinkDialog plan={plan} onClose={() => setOpen(null)} />}
      </>
    );
  }

  const content = (
    <>
      <span className="text-muted-foreground">NOS Demand:</span>
      <span className="max-w-64 truncate font-medium">{link.demandName ?? "Untitled Demand"}</span>
      {link.closedThrough !== null && (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Lock className="h-3 w-3" aria-hidden />
          to {formatMonthShort(link.closedThrough)}
        </span>
      )}
      {link.editsToPost === true && (
        <span className="inline-flex items-center gap-1.5 font-medium text-warning-700 dark:text-warning-400">
          <StatusDot className="bg-warning" />
          Edits to post
        </span>
      )}
    </>
  );
  const title = `${link.demandName ?? "This NOS Demand"}${
    link.closedThrough === null
      ? " — none of this plan's months are closed in NOS yet"
      : ` — months through ${formatMonthShort(link.closedThrough)} are closed in NOS and locked`
  }${link.postedAt === null ? "" : `. Last posted to NOS ${new Date(link.postedAt).toLocaleString()}`}${
    link.editsToPost === true
      ? ". The plan's open-month hours differ from what NOS was last given: Post to NOS sends them"
      : ""
  }`;

  if (!canEdit) {
    return (
      <span
        className={cn(STATUS_TRIGGER_CLASS, "cursor-default hover:bg-transparent")}
        title={title}
      >
        {content}
      </span>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={STATUS_TRIGGER_CLASS} title={title}>
            {content}
            <ChevronDown className="size-4 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => setOpen("post")}>
            <Send className="h-4 w-4" />
            Post to NOS…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setOpen("refresh")}>
            <RefreshCw className="h-4 w-4" />
            Refresh closed months from NOS
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setOpen("link")}
            disabled={linkBlock !== null}
            title={linkBlock ?? undefined}
          >
            <Link2 className="h-4 w-4" />
            {linkBlock === null ? "Change NOS Demand…" : "Change NOS Demand · once Staffed"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setOpen("unlink")}>
            <Unlink className="h-4 w-4" />
            Unlink
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {open === "link" && <LinkDialog plan={plan} onClose={() => setOpen(null)} />}
      {open === "refresh" && (
        <LinkDialog plan={plan} refreshDemandId={link.demandId} onClose={() => setOpen(null)} />
      )}
      {open === "post" && (
        <PostDialog
          plan={plan}
          onRefresh={() => setOpen("refresh")}
          onClose={() => setOpen(null)}
        />
      )}
      {open === "unlink" && <UnlinkDialog plan={plan} onClose={() => setOpen(null)} />}
    </>
  );
}

// ── Link / refresh ───────────────────────────────────────────────────────────

function LinkDialog({
  plan,
  refreshDemandId,
  onClose,
}: {
  plan: ResourcePlanDetailView;
  /** Set to re-take NOS's numbers for the Demand already linked (no picker). */
  refreshDemandId?: string;
  onClose: () => void;
}) {
  const refresh = refreshDemandId !== undefined;
  const options = usePlanNosDemandOptions(plan.id, !refresh);
  const linkDemand = useLinkNosDemand(plan.id);
  const [demandId, setDemandId] = useState(refreshDemandId ?? "");
  const preview = useNosDemandLinkPreview(plan.id, demandId);
  // Creating is for a plan with no Demand yet, never to replace one.
  const createUrl =
    !refresh && (plan.nosDemand ?? null) === null ? (options.data?.createDemandUrl ?? null) : null;

  const free = (options.data?.demands ?? []).filter((d) => d.linkedPlan === null);
  const taken = (options.data?.demands ?? []).length - free.length;
  const shown = preview.data ?? null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {refresh
              ? "Refresh closed months from NOS"
              : (plan.nosDemand ?? null) === null
                ? "Link a NOS Demand"
                : "Change the NOS Demand"}
          </DialogTitle>
          <DialogDescription>
            {refresh
              ? "Months NOS has closed since the last refresh take NOS's hours and lock."
              : `Pick one of ${plan.clientAccountName}'s NOS Demands. Months NOS has closed take NOS's hours and lock; open months keep the plan's hours. Nothing is written to NOS.`}
          </DialogDescription>
        </DialogHeader>

        {!refresh && (
          <div className="flex flex-col gap-1.5">
            <SearchableSelect
              options={free.map((d) => ({
                value: d.id,
                label: `${d.name}${d.status === null ? "" : ` · ${d.status}`}`,
              }))}
              value={demandId}
              onValueChange={setDemandId}
              loading={options.isLoading}
              placeholder="Pick the NOS Demand"
              searchPlaceholder="Search Demands…"
              emptyMessage={`No active NOS Demand on ${plan.clientAccountName} matches that.`}
            />
            {options.isError && (
              <ErrorState
                message="Couldn’t load NOS Demands right now."
                onRetry={() => options.refetch()}
              />
            )}
            {taken > 0 && (
              <p className="text-xs text-muted-foreground">
                {taken} Demand{taken === 1 ? " is" : "s are"} already linked to another plan and not
                listed.
              </p>
            )}
            {createUrl !== null && (
              <Button variant="link" size="sm" className="self-start px-0" asChild>
                <a href={createUrl} target="_blank" rel="noreferrer">
                  <Plus className="h-4 w-4" aria-hidden />
                  Create a new Demand in Demand Planning
                </a>
              </Button>
            )}
          </div>
        )}

        {preview.isFetching && <p className="text-sm text-muted-foreground">Reading NOS…</p>}
        {preview.isError && (
          <ErrorState
            message={preview.error.message || "Couldn’t read that NOS Demand right now."}
            onRetry={() => preview.refetch()}
          />
        )}
        {shown !== null && !preview.isFetching && <LinkPreviewBody preview={shown} />}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={shown === null || preview.isFetching || linkDemand.isPending}
            onClick={() =>
              shown !== null &&
              linkDemand.mutate(
                { demandId: shown.demand.id, previewToken: shown.previewToken, refresh },
                { onSuccess: onClose },
              )
            }
          >
            {linkDemand.isPending ? "Saving…" : refresh ? "Refresh" : "Link NOS Demand"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkPreviewBody({ preview }: { preview: PlanNosDemandLinkPreview }) {
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p>
        {preview.lockedMonths.length === 0 ? (
          preview.closedMonthsUnknown ? (
            "NOS has nobody on this Demand yet, so no months are locked."
          ) : (
            "None of this plan's months are closed in NOS yet. Nothing locks."
          )
        ) : (
          <>
            <span className="font-medium">{monthSpan(preview.lockedMonths)}</span> are closed in
            NOS. They take NOS's hours and lock.
          </>
        )}
      </p>
      {preview.switchesToMonthByMonth && (
        <Alert>
          <AlertDescription>
            This plan is one steady figure per month. It switches to month by month, because closed
            months need their own figures.
          </AlertDescription>
        </Alert>
      )}

      {preview.changes.length > 0 ? (
        <div className="max-h-64 overflow-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5 text-left font-medium">Line</th>
                <th className="px-3 py-1.5 text-left font-medium">Month</th>
                <th className="px-3 py-1.5 text-right font-medium">Plan hrs</th>
                <th className="px-3 py-1.5 text-right font-medium">NOS hrs</th>
              </tr>
            </thead>
            <tbody>
              {preview.changes.map((c) => (
                <tr key={`${c.lineLabel}|${c.month}`} className="border-t border-border">
                  <td className="px-3 py-1.5">{c.lineLabel}</td>
                  <td className="px-3 py-1.5">{formatMonthShort(c.month)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground line-through">
                    {trimHours(c.fromHours)}
                  </td>
                  <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                    {trimHours(c.toHours)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        preview.lockedMonths.length > 0 && (
          <p className="text-muted-foreground">The plan already has NOS's hours for them.</p>
        )
      )}

      {preview.notOnPlan.length > 0 && (
        <div>
          <p className="font-medium">NOS has closed-month hours for Resources not on this plan</p>
          <p className="text-xs text-muted-foreground">
            Listed for you to check. Their hours aren't added to the plan.
          </p>
          <ul className="mt-1 list-disc pl-5">
            {preview.notOnPlan.map((p) => (
              <li key={p.name}>
                {p.name} — {trimHours(p.hours)} hrs ({monthSpan(p.months)})
              </li>
            ))}
          </ul>
        </div>
      )}
      {preview.rampResetMonths.length > 0 && (
        <p className="text-xs text-muted-foreground">
          The ramp on {preview.rampResetMonths.map(formatMonthShort).join(", ")} goes back to full:
          NOS's hours are the hours.
        </p>
      )}
    </div>
  );
}

// ── Post to NOS ──────────────────────────────────────────────────────────────

function PostDialog({
  plan,
  onRefresh,
  onClose,
}: {
  plan: ResourcePlanDetailView;
  onRefresh: () => void;
  onClose: () => void;
}) {
  const preview = useNosGuidancePostPreview(plan.id, true);
  const post = usePostNosGuidance(plan.id);
  const data = preview.data;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Post to NOS</DialogTitle>
          <DialogDescription>
            {data === undefined || data.openMonths.length === 0
              ? `Overwrites the guidance on ${plan.nosDemand?.demandName ?? "the NOS Demand"} for the plan's open months. Months NOS has closed are never touched.`
              : `Overwrites the guidance on ${data.demand.name ?? "the NOS Demand"} for ${monthSpan(data.openMonths)} with the plan's hours, including zero for Resources no longer on the plan. Months NOS has closed are never touched.`}
          </DialogDescription>
        </DialogHeader>

        {preview.isLoading && <p className="text-sm text-muted-foreground">Reading NOS…</p>}
        {preview.isError && (
          <ErrorState
            message={preview.error.message || "Couldn’t read NOS right now."}
            onRetry={() => preview.refetch()}
          />
        )}

        {data !== undefined && (
          <div className="flex flex-col gap-3 text-sm">
            {data.blockedReason !== null && (
              <Alert
                variant={
                  data.rows.length === 0 && data.unnamedLines.length === 0
                    ? "default"
                    : "destructive"
                }
              >
                <AlertDescription>
                  <p>{data.blockedReason}</p>
                  {data.unnamedLines.length > 0 && (
                    <ul className="mt-1 list-disc pl-5">
                      {data.unnamedLines.map((l) => (
                        <li key={l.lineLabel}>
                          {l.lineLabel} — {trimHours(l.hours)} hrs
                        </li>
                      ))}
                    </ul>
                  )}
                  {data.unnamedLines.length === 0 && data.unmappedResources.length > 0 && (
                    <ul className="mt-1 list-disc pl-5">
                      {data.unmappedResources.map((r) => (
                        <li key={r.name}>
                          {r.name} — {trimHours(r.hours)} hrs
                        </li>
                      ))}
                    </ul>
                  )}
                  {data.newlyClosedMonths.length > 0 && (
                    <Button size="sm" variant="outline" className="mt-2" onClick={onRefresh}>
                      <RefreshCw className="h-4 w-4" />
                      Refresh closed months from NOS
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {data.rows.length > 0 && (
              <div className="max-h-72 overflow-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">Resource</th>
                      <th className="px-3 py-1.5 text-left font-medium">Month</th>
                      <th className="px-3 py-1.5 text-right font-medium">NOS now</th>
                      <th className="px-3 py-1.5 text-right font-medium">After posting</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={`${r.resourceName}|${r.month}`} className="border-t border-border">
                        <td className="px-3 py-1.5">
                          {r.resourceName}
                          {r.addsResource && (
                            <span className="ml-1.5 text-xs text-primary">added to Demand</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5">{formatMonthShort(r.month)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                          {r.nosHours === null ? "—" : trimHours(r.nosHours)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                          {trimHours(r.planHours)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {data.addsResources.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {data.addsResources.join(", ")}{" "}
                {data.addsResources.length === 1 ? "isn't" : "aren't"} on the Demand yet. Posting
                adds them with their NOS role and the plan line's bill rate.
              </p>
            )}
            {data.monthsMissingInNos.length > 0 && (
              <p className="text-xs text-muted-foreground">
                NOS has no month for {data.monthsMissingInNos.map(formatMonthShort).join(", ")} yet,
                so {data.monthsMissingInNos.length === 1 ? "it isn't" : "they aren't"} posted.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={data === undefined || data.blockedReason !== null || post.isPending}
            onClick={() =>
              data !== undefined && post.mutate(data.previewToken, { onSuccess: onClose })
            }
          >
            <Send className="h-4 w-4" />
            {post.isPending
              ? "Posting…"
              : data !== undefined && data.rows.length > 0
                ? `Post ${data.rows.length} change${data.rows.length === 1 ? "" : "s"}`
                : "Post to NOS"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Unlink ───────────────────────────────────────────────────────────────────

function UnlinkDialog({ plan, onClose }: { plan: ResourcePlanDetailView; onClose: () => void }) {
  const unlink = useUnlinkNosDemand(plan.id);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unlink the NOS Demand?</DialogTitle>
          <DialogDescription>
            The plan keeps its hours and its months unlock. Nothing in NOS changes:{" "}
            {plan.nosDemand?.demandName ?? "the Demand"} and its guidance stay as they are.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={unlink.isPending}
            onClick={() => unlink.mutate(undefined, { onSuccess: onClose })}
          >
            {unlink.isPending ? "Unlinking…" : "Unlink"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
